/**
 * Ported from hd_conf/src/lib/classes/ExtrudeGeometryUncapped.tsx
 * Original: https://github.com/mrdoob/three.js/blob/master/src/geometries/ExtrudeGeometry.js
 *
 * Modified to:
 * - Not build end caps (no lids)
 * - Not generate UVs or normals by default
 * - Support `extrudePathIsClosed`, `extrudePathGetUnspacedPoints`, `copyLastToFirst`
 * - Support `segmentSplitPositions` for multi-material rings
 * - Support `deleteInnerFaces`
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
    BufferGeometry,
    Curve,
    ExtrudeGeometryOptions,
    Float32BufferAttribute,
    NormalBufferAttributes,
    Shape,
    ShapeUtils,
    Vector2,
    Vector3,
} from 'three';

const ROUND_TO_DECIMAL_PLACE = 6;

function roundNumToDecimal(value: number, decimalPlace: number): number {
    const factor = Math.pow(10, decimalPlace);
    return Math.round(value * factor) / factor;
}

export interface ExtrudeGeometryUncappedOptions extends ExtrudeGeometryOptions {
    extrudePathIsClosed?: boolean;
    extrudePathGetUnspacedPoints?: boolean;
    copyLastToFirst?: boolean;
    segmentSplitPositions?: number[];
    deleteInnerFaces?: boolean;
    computeVertexNormals?: boolean;
}

class ExtrudeGeometryUncapped extends BufferGeometry {
    constructor(shapes: Shape | Shape[], options: ExtrudeGeometryUncappedOptions) {
        super();
        Object.defineProperty(this, 'type', { value: 'ExtrudeGeometryUncapped' });

        if (!shapes || (Array.isArray(shapes) && !shapes.length)) return;

        const shapeArray = Array.isArray(shapes) ? shapes : [shapes];

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const scope: BufferGeometry<NormalBufferAttributes> = this;
        const computeVertexNormals = options.computeVertexNormals;
        const verticesArray: number[] = [];

        for (let i = 0; i < shapeArray.length; i++) {
            addShape(shapeArray[i], i);
        }

        scope.setAttribute('position', new Float32BufferAttribute(verticesArray, 3));

        if (computeVertexNormals) {
            scope.computeVertexNormals();
        }

        function addShape(shape: Shape, shapeNumber: number) {
            const placeholder: number[] = [];

            const curveSegments = options.curveSegments ?? 12;
            const steps = options.steps ?? 1;
            const depth = options.depth ?? 1;

            let bevelEnabled = options.bevelEnabled ?? true;
            let bevelThickness = options.bevelThickness ?? 0.2;
            let bevelSize = options.bevelSize ?? bevelThickness - 0.1;
            let bevelOffset = options.bevelOffset ?? 0;
            let bevelSegments = options.bevelSegments ?? 3;

            const extrudePath = options.extrudePath;
            const extrudePathIsClosed = options.extrudePathIsClosed;
            const copyLastToFirst = options.copyLastToFirst;
            const getUnspacedPoints = options.extrudePathGetUnspacedPoints;
            const segmentSplitPositions = options.segmentSplitPositions;
            const deleteInnerFaces = options.deleteInnerFaces ?? true;

            let extrudePts: Vector3[] | undefined;
            let extrudeByPath = false;
            let splineTube: ReturnType<Curve<Vector3>['computeFrenetFrames']> | undefined;
            let binormal: Vector3 | undefined;
            let normal: Vector3 | undefined;
            let position2: Vector3 | undefined;

            if (extrudePath) {
                extrudePts = getUnspacedPoints
                    ? extrudePath.getPoints(steps)
                    : extrudePath.getSpacedPoints(steps);

                extrudeByPath = true;
                bevelEnabled = false;

                splineTube = extrudePath.computeFrenetFrames(steps, extrudePathIsClosed);
                binormal = new Vector3();
                normal = new Vector3();
                position2 = new Vector3();
            }

            if (!bevelEnabled) {
                bevelSegments = 0;
                bevelThickness = 0;
                bevelSize = 0;
                bevelOffset = 0;
            }

            const shapePoints = shape.extractPoints(curveSegments);
            let vertices = shapePoints.shape;
            const holes = shapePoints.holes;

            const reverse = !ShapeUtils.isClockWise(vertices);
            if (reverse) {
                vertices = vertices.reverse();
                for (let h = 0; h < holes.length; h++) {
                    if (ShapeUtils.isClockWise(holes[h])) holes[h] = holes[h].reverse();
                }
            }

            const contour = vertices;
            for (let h = 0; h < holes.length; h++) {
                vertices = vertices.concat(holes[h]);
            }

            function scalePt2(pt: Vector2, vec: Vector2, size: number) {
                return pt.clone().addScaledVector(vec, size);
            }

            const vlen = vertices.length;

            function getBevelVec(inPt: Vector2, inPrev: Vector2, inNext: Vector2): Vector2 {
                let v_trans_x: number, v_trans_y: number, shrink_by: number;
                const v_prev_x = inPt.x - inPrev.x;
                const v_prev_y = inPt.y - inPrev.y;
                const v_next_x = inNext.x - inPt.x;
                const v_next_y = inNext.y - inPt.y;
                const v_prev_lensq = v_prev_x * v_prev_x + v_prev_y * v_prev_y;
                const collinear0 = v_prev_x * v_next_y - v_prev_y * v_next_x;

                if (Math.abs(collinear0) > Number.EPSILON) {
                    const v_prev_len = Math.sqrt(v_prev_lensq);
                    const v_next_len = Math.sqrt(v_next_x * v_next_x + v_next_y * v_next_y);
                    const ptPrevShift_x = inPrev.x - v_prev_y / v_prev_len;
                    const ptPrevShift_y = inPrev.y + v_prev_x / v_prev_len;
                    const ptNextShift_x = inNext.x - v_next_y / v_next_len;
                    const ptNextShift_y = inNext.y + v_next_x / v_next_len;
                    const sf =
                        ((ptNextShift_x - ptPrevShift_x) * v_next_y - (ptNextShift_y - ptPrevShift_y) * v_next_x) /
                        (v_prev_x * v_next_y - v_prev_y * v_next_x);
                    v_trans_x = ptPrevShift_x + v_prev_x * sf - inPt.x;
                    v_trans_y = ptPrevShift_y + v_prev_y * sf - inPt.y;
                    const v_trans_lensq = v_trans_x * v_trans_x + v_trans_y * v_trans_y;
                    if (v_trans_lensq <= 2) return new Vector2(v_trans_x, v_trans_y);
                    shrink_by = Math.sqrt(v_trans_lensq / 2);
                } else {
                    let direction_eq: boolean;
                    if (v_prev_x > Number.EPSILON) {
                        direction_eq = v_next_x > Number.EPSILON;
                    } else if (v_prev_x < -Number.EPSILON) {
                        direction_eq = v_next_x < -Number.EPSILON;
                    } else {
                        direction_eq = Math.sign(v_prev_y) === Math.sign(v_next_y);
                    }
                    if (direction_eq) {
                        v_trans_x = -v_prev_y;
                        v_trans_y = v_prev_x;
                        shrink_by = Math.sqrt(v_prev_lensq);
                    } else {
                        v_trans_x = v_prev_x;
                        v_trans_y = v_prev_y;
                        shrink_by = Math.sqrt(v_prev_lensq / 2);
                    }
                }
                return new Vector2(v_trans_x! / shrink_by!, v_trans_y! / shrink_by!);
            }

            const contourMovements: Vector2[] = [];
            for (let i = 0, il = contour.length, j = il - 1, k = i + 1; i < il; i++, j++, k++) {
                if (j === il) j = 0;
                if (k === il) k = 0;
                contourMovements[i] = getBevelVec(contour[i], contour[j], contour[k]);
            }

            const holesMovements: Vector2[][] = [];
            let oneHoleMovements: Vector2[];
            let verticesMovements = contourMovements.concat();
            for (let h = 0; h < holes.length; h++) {
                const ahole = holes[h];
                oneHoleMovements = [];
                for (let i = 0, il = ahole.length, j = il - 1, k = i + 1; i < il; i++, j++, k++) {
                    if (j === il) j = 0;
                    if (k === il) k = 0;
                    oneHoleMovements[i] = getBevelVec(ahole[i], ahole[j], ahole[k]);
                }
                holesMovements.push(oneHoleMovements);
                verticesMovements = verticesMovements.concat(oneHoleMovements);
            }

            // Bevel layers
            for (let b = 0; b < bevelSegments; b++) {
                const t = b / bevelSegments;
                const z = bevelThickness * Math.cos((t * Math.PI) / 2);
                const bs = bevelSize * Math.sin((t * Math.PI) / 2) + bevelOffset;
                for (let i = 0; i < contour.length; i++) {
                    const vert = scalePt2(contour[i], contourMovements[i], bs);
                    v(vert.x, vert.y, -z);
                }
                for (let h = 0; h < holes.length; h++) {
                    const ahole = holes[h];
                    oneHoleMovements = holesMovements[h];
                    for (let i = 0; i < ahole.length; i++) {
                        const vert = scalePt2(ahole[i], oneHoleMovements[i], bs);
                        v(vert.x, vert.y, -z);
                    }
                }
            }

            const bs = bevelSize + bevelOffset;
            const bfVerts: Vector3[] = [];

            // Back-facing vertices
            for (let i = 0; i < vlen; i++) {
                const vert = bevelEnabled ? scalePt2(vertices[i], verticesMovements[i], bs) : vertices[i];
                if (!extrudeByPath) {
                    v(vert.x, vert.y, 0);
                } else {
                    normal!.copy(splineTube!.normals[0]).multiplyScalar(vert.x);
                    binormal!.copy(splineTube!.binormals[0]).multiplyScalar(vert.y);
                    position2!.copy(extrudePts![0]).add(normal!).add(binormal!);
                    v(position2!.x, position2!.y, position2!.z);
                    if (copyLastToFirst) bfVerts.push(position2!.clone());
                }
            }

            // Stepped vertices
            for (let s = 1; s <= steps; s++) {
                for (let i = 0; i < vlen; i++) {
                    const vert = bevelEnabled ? scalePt2(vertices[i], verticesMovements[i], bs) : vertices[i];
                    if (!extrudeByPath) {
                        v(vert.x, vert.y, (depth / steps) * s);
                    } else {
                        normal!.copy(splineTube!.normals[s]).multiplyScalar(vert.x);
                        binormal!.copy(splineTube!.binormals[s]).multiplyScalar(vert.y);
                        position2!.copy(extrudePts![s]).add(normal!).add(binormal!);
                        if (copyLastToFirst && s === steps) {
                            v(bfVerts[i].x, bfVerts[i].y, bfVerts[i].z);
                        } else {
                            v(position2!.x, position2!.y, position2!.z);
                        }
                    }
                }
            }

            // Back bevel layers
            for (let b = bevelSegments - 1; b >= 0; b--) {
                const t = b / bevelSegments;
                const z = bevelThickness * Math.cos((t * Math.PI) / 2);
                const bs = bevelSize * Math.sin((t * Math.PI) / 2) + bevelOffset;
                for (let i = 0; i < contour.length; i++) {
                    const vert = scalePt2(contour[i], contourMovements[i], bs);
                    v(vert.x, vert.y, depth + z);
                }
                for (let h = 0; h < holes.length; h++) {
                    const ahole = holes[h];
                    oneHoleMovements = holesMovements[h];
                    for (let i = 0; i < ahole.length; i++) {
                        const vert = scalePt2(ahole[i], oneHoleMovements[i], bs);
                        if (!extrudeByPath) {
                            v(vert.x, vert.y, depth + z);
                        } else {
                            v(vert.x, vert.y + extrudePts![steps - 1].y, extrudePts![steps - 1].x + z);
                        }
                    }
                }
            }

            buildSideFaces();

            function buildSideFaces() {
                const start = verticesArray.length / 3;
                let layeroffset = 0;
                sidewalls(contour, layeroffset);
                layeroffset += contour.length;
                for (let h = 0; h < holes.length; h++) {
                    sidewalls(holes[h], layeroffset);
                    layeroffset += holes[h].length;
                }
                scope.addGroup(start, verticesArray.length / 3 - start, shapeNumber);
            }

            function sidewalls(contour: any[], layeroffset: number) {
                let i = contour.length;
                while (--i >= 0) {
                    const j = i;
                    const k = i - 1 < 0 ? contour.length - 1 : i - 1;
                    for (let s = 0, sl = steps + bevelSegments * 2; s < sl; s++) {
                        const slen1 = vlen * s;
                        const slen2 = vlen * (s + 1);
                        const a = layeroffset + j + slen1;
                        const b = layeroffset + k + slen1;
                        const c = layeroffset + k + slen2;
                        const d = layeroffset + j + slen2;
                        f4(a, b, c, d);
                    }
                }
            }

            function v(x: number, y: number, z: number) {
                placeholder.push(roundNumToDecimal(x, ROUND_TO_DECIMAL_PLACE));
                placeholder.push(roundNumToDecimal(y, ROUND_TO_DECIMAL_PLACE));
                placeholder.push(roundNumToDecimal(z, ROUND_TO_DECIMAL_PLACE));
            }

            function f4(a: number, b: number, c: number, d: number) {
                if (deleteInnerFaces && segmentSplitPositions?.length) {
                    if (!areTrisOnSegmentPlane([a, b, c, d], placeholder, segmentSplitPositions, shapeNumber)) {
                        addQuad(a, b, c, d);
                    }
                } else {
                    addQuad(a, b, c, d);
                }
            }

            function addQuad(a: number, b: number, c: number, d: number) {
                addVertex(a); addVertex(b); addVertex(d);
                addVertex(b); addVertex(c); addVertex(d);
            }

            function addVertex(index: number) {
                verticesArray.push(placeholder[index * 3]);
                verticesArray.push(placeholder[index * 3 + 1]);
                verticesArray.push(placeholder[index * 3 + 2]);
            }
        }
    }
}

export default ExtrudeGeometryUncapped;

function areTrisOnSegmentPlane(
    vertexIndices: [number, number, number, number],
    placeholder: number[],
    segmentSplitPositions: number[],
    shapeNumber: number,
): boolean {
    for (const segPos of segmentSplitPositions) {
        const rounded = roundNumToDecimal(segPos, ROUND_TO_DECIMAL_PLACE);
        let allOnPlane = true;
        for (const idx of vertexIndices) {
            const z = placeholder[idx * 3 + 2];
            if (Math.abs(z - rounded) > 1e-5) { allOnPlane = false; break; }
        }
        if (allOnPlane && shapeNumber > 0) return true;
    }
    return false;
}
